import { useId, useRef, useState } from 'react';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  ReactElement,
  RefObject,
} from 'react';
import type { TargetCandidate, TargetCardinality } from '@cards/engine';
import { Button } from './kit/index.js';

export interface TargetPickerOption {
  readonly choiceToken: string;
  readonly candidate: TargetCandidate;
  readonly label?: string;
}

export interface TargetPickerProps {
  readonly candidates: readonly TargetPickerOption[];
  readonly cardinality: TargetCardinality;
  readonly ordered?: boolean;
  readonly title?: string;
  readonly description?: string;
  /** Planning may further restrict cardinality-valid tuples to enumerated actions. */
  readonly isSelectionAllowed?: (choiceTokens: readonly string[]) => boolean;
  readonly onConfirm: (choiceTokens: readonly string[]) => void;
  /** Runtime choices deliberately omit this: only cancellable local dialogs pass it. */
  readonly onCancel?: () => void;
  readonly dialogTitleId?: string;
  readonly dialogDescriptionId?: string;
  readonly modalDialogRef?: RefObject<HTMLElement | null>;
}

function assertNever(candidate: never): never {
  throw new Error(`TargetPicker: unknown target kind "${String((candidate as { kind?: unknown }).kind)}"`);
}

function genericCandidateLabel(candidate: TargetCandidate): string {
  switch (candidate.kind) {
    case 'unit':
      return `Боец ${candidate.instanceId}`;
    case 'card':
      return `Карта ${candidate.instanceId} · зона ${candidate.zone}`;
    case 'cell':
      return `Ячейка ${candidate.cellId}`;
    case 'table':
      return `Локация ${candidate.tableId}`;
    case 'player':
      return `Игрок ${candidate.playerId}`;
    case 'mode':
      return `Режим ${candidate.id}`;
    default:
      return assertNever(candidate);
  }
}

function publicCandidateKey(candidate: TargetCandidate): string {
  switch (candidate.kind) {
    case 'unit':
      return `unit\u0000${candidate.instanceId}`;
    case 'card':
      return `card\u0000${candidate.instanceId}\u0000${candidate.zone}`;
    case 'cell':
      return `cell\u0000${candidate.cellId}`;
    case 'table':
      return `table\u0000${candidate.tableId}`;
    case 'player':
      return `player\u0000${candidate.playerId}`;
    case 'mode':
      return `mode\u0000${candidate.id}`;
    default:
      return assertNever(candidate);
  }
}

function validateDomain(
  candidates: readonly TargetPickerOption[],
  cardinality: TargetCardinality,
): void {
  if (!Number.isSafeInteger(cardinality.min) || !Number.isSafeInteger(cardinality.max)
    || cardinality.min < 0 || cardinality.max < cardinality.min) {
    throw new Error('TargetPicker: cardinality должна содержать safe integer 0 <= min <= max');
  }

  const tokens = new Set<string>();
  const publicCandidates = new Set<string>();
  for (const option of candidates) {
    if (typeof option.choiceToken !== 'string' || option.choiceToken.length === 0) {
      throw new Error('TargetPicker: каждый вариант требует непустой choiceToken');
    }
    if (tokens.has(option.choiceToken)) {
      throw new Error('TargetPicker: choiceToken вариантов должны быть уникальны');
    }
    tokens.add(option.choiceToken);

    const key = publicCandidateKey(option.candidate);
    if (publicCandidates.has(key)) {
      throw new Error('TargetPicker: public candidates должны быть уникальны');
    }
    publicCandidates.add(key);
  }
}

function selectionStatus(count: number, cardinality: TargetCardinality): string {
  if (cardinality.min === cardinality.max) {
    return `Выбрано ${count} из ${cardinality.max}`;
  }
  return `Выбрано ${count}. Минимум ${cardinality.min}, максимум ${cardinality.max}`;
}

export function TargetPicker({
  candidates,
  cardinality,
  ordered = false,
  title = 'Выберите цель',
  description,
  isSelectionAllowed,
  onConfirm,
  onCancel,
  dialogTitleId,
  dialogDescriptionId,
  modalDialogRef,
}: TargetPickerProps): ReactElement {
  validateDomain(candidates, cardinality);

  const [selectedIndexes, setSelectedIndexes] = useState<readonly number[]>([]);
  const generatedTitleId = useId();
  const generatedDescriptionId = useId();
  const titleId = dialogTitleId ?? generatedTitleId;
  const descriptionId = dialogDescriptionId ?? generatedDescriptionId;
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  function toggle(index: number): void {
    setSelectedIndexes((previous) => {
      if (previous.includes(index)) return previous.filter((selected) => selected !== index);
      if (previous.length >= cardinality.max) return previous;
      return [...previous, index];
    });
  }

  function tokensFor(indexes: readonly number[]): readonly string[] {
    const canonicalIndexes = ordered ? indexes : [...indexes].sort((left, right) => left - right);
    return canonicalIndexes.map((index) => candidates[index]!.choiceToken);
  }

  const selectionTokens = tokensFor(selectedIndexes);
  const ready = selectedIndexes.length >= cardinality.min
    && selectedIndexes.length <= cardinality.max
    && (isSelectionAllowed?.(selectionTokens) ?? true);
  const status = selectionStatus(selectedIndexes.length, cardinality);

  function confirm(): void {
    if (!ready) return;
    onConfirm(selectionTokens);
  }

  function moveOptionFocus(event: ReactKeyboardEvent<HTMLButtonElement>, index: number): void {
    const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown'
      ? 1
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
        ? -1
        : 0;
    if (direction === 0 && event.key !== 'Home' && event.key !== 'End') return;
    const enabled = optionRefs.current
      .map((element, optionIndex) => ({ element, optionIndex }))
      .filter((item): item is { element: HTMLButtonElement; optionIndex: number } =>
        item.element !== null && !item.element.disabled);
    if (enabled.length === 0) return;
    event.preventDefault();
    if (event.key === 'Home') {
      enabled[0]!.element.focus();
      return;
    }
    if (event.key === 'End') {
      enabled.at(-1)!.element.focus();
      return;
    }
    const position = enabled.findIndex((item) => item.optionIndex === index);
    const nextPosition = (position + direction + enabled.length) % enabled.length;
    enabled[nextPosition]!.element.focus();
  }

  return (
    <section
      ref={(node) => {
        if (modalDialogRef !== undefined) modalDialogRef.current = node;
      }}
      className="cards-target-picker"
      data-testid="target-picker"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <h2 id={titleId}>
        {title}
      </h2>
      <p id={descriptionId} className="cards-target-picker-description">
        {description ?? (ordered
          ? 'Порядок выбора влияет на результат.'
          : 'Порядок выбора не влияет на результат.')}
      </p>

      <div
        className="cards-target-picker-options"
        role="group"
        aria-label="Доступные варианты"
      >
        {candidates.map((option, index) => {
          const selected = selectedIndexes.includes(index);
          const position = ordered && selected ? selectedIndexes.indexOf(index) + 1 : undefined;
          const customLabel = option.label?.trim();
          const label = customLabel === undefined || customLabel.length === 0
            ? genericCandidateLabel(option.candidate)
            : customLabel;
          const maxReached = selectedIndexes.length >= cardinality.max;
          return (
            <button
              key={index}
              ref={(element) => {
                optionRefs.current[index] = element;
              }}
              type="button"
              className="cards-target-picker-option"
              data-testid={`target-option-${index}`}
              data-target-kind={option.candidate.kind}
              aria-pressed={selected}
              disabled={!selected && maxReached}
              onClick={() => toggle(index)}
              onKeyDown={(event) => moveOptionFocus(event, index)}
            >
              <span>{label}</span>
              {position === undefined ? null : (
                <span
                  data-testid={`target-option-position-${index}`}
                  aria-label={`Позиция ${position}`}
                  className="cards-target-picker-position"
                >
                  {position}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div
        data-testid="target-picker-status"
        role="status"
        aria-live="polite"
        className="cards-target-picker-status"
      >
        {status}
      </div>
      <div className="cards-target-picker-actions">
        <Button
          variant={ready ? 'primary' : 'secondary'}
          size="sm"
          className="cards-target-picker-confirm"
          data-testid="target-picker-confirm"
          disabled={!ready}
          onClick={confirm}
        >
          {cardinality.min === 0 && selectedIndexes.length === 0 ? 'Подтвердить без выбора' : 'Подтвердить'}
        </Button>
        {onCancel === undefined ? null : (
          <Button
            variant="secondary"
            size="sm"
            className="cards-target-picker-cancel"
            data-testid="target-picker-cancel"
            onClick={onCancel}
          >
            Отменить выбор
          </Button>
        )}
      </div>
    </section>
  );
}
